import {
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isNonNullish } from 'remeda';
import { Subscription } from 'rxjs';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import {
  ModalContainer,
  ModalList,
  type ModalListCreateAction,
  type ModalListFilter,
  type ModalListItem,
} from 'src/components/ModalContainer';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { wsEventBus } from 'src/core/realtime-connection';
import {
  SidekickChatSession,
  SidekickRun,
  SidekickSessionRun,
  useSidekickChatSessions,
  useSidekickRunResult,
  useSidekickSessionRuns,
  useSidekickSuggestedQuestions,
  useStartSidekickRun,
} from 'src/graphql';
import { useNotifications } from 'src/utils/hooks';
import { useCopyToClipboard } from 'src/utils/hooks/use-copy-to-clipboard';
import { type AssistantBubbleState, AssistantContent } from './assistant-content';
import { Bubble, MessageWithMeta } from './chat-message';
import {
  chatInputSx,
  conversationContextSx,
  getChatInputContainerSx,
  getSendButtonSx,
  historyIconButtonSx,
} from './sidekick-tab.styles';
import {
  extractSidekickExtra,
  getAssistantCopyContent,
  mergeSessions,
} from './sidekick-utils';
import { SuggestedQuestions } from './suggested-questions';

interface SidekickTabProps {
  friendlyConversationId?: string;
  conversationId?: string;
  partnerId?: number | null;
  conversationSubject?: string;
  historyOpen?: boolean;
  onHistoryOpenChange?: (open: boolean) => void;
}

export const SidekickTab = ({
  conversationSubject,
  friendlyConversationId,
  conversationId,
  partnerId,
  historyOpen: historyOpenProp,
  onHistoryOpenChange,
}: SidekickTabProps) => {
  const { showErrorAlert } = useNotifications();
  const [agentInput, setAgentInput] = useState('');
  const [agentMessages, setAgentMessages] = useState<Record<string, string>>({});
  const [assistantByRun, setAssistantByRun] = useState<Record<string, AssistantBubbleState>>(
    {},
  );
  const [runMap, setRunMap] = useState<Record<string, SidekickRun>>({});
  const [fetchedResults, setFetchedResults] = useState<Set<string>>(new Set());
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const sessionRunIdsRef = useRef<Set<string>>(new Set());
  const sessionScopeRef = useRef<string | null>(null);
  const scrollAnchorRef = useRef<HTMLDivElement | null>(null);
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const isStartingNewChatRef = useRef(false);
  const isHistoryControlled = historyOpenProp !== undefined;
  const [internalHistoryOpen, setInternalHistoryOpen] = useState(false);
  const historyOpen = isHistoryControlled ? historyOpenProp : internalHistoryOpen;
  const setHistoryOpen = useCallback(
    (open: boolean) => {
      if (!isHistoryControlled) {
        setInternalHistoryOpen(open);
      }
      onHistoryOpenChange?.(open);
    },
    [isHistoryControlled, onHistoryOpenChange],
  );

  const [startRunMutation, { loading: isSubmitting }] = useStartSidekickRun();
  const [fetchRunResult] = useSidekickRunResult();
  const { data: suggestedQuestionsData, loading: isSuggestionsLoading } =
    useSidekickSuggestedQuestions(conversationId, partnerId);
  const {
    data: sessionsData,
    refetch: refetchSessions,
    loading: sessionsLoading,
  } = useSidekickChatSessions(conversationId, partnerId);
  const [loadSessionRuns, { data: sessionRunsData }] = useSidekickSessionRuns();
  const partnerSelected = isNonNullish(partnerId);
  const hasConversationContext = Boolean(conversationId && partnerSelected);
  const suggestedQuestions =
    suggestedQuestionsData?.getSidekickSuggestedQuestions?.questions ?? [];
  const { copyToClipboard } = useCopyToClipboard({ successMessage: 'Message copied' });

  const baseSessions = useMemo(
    () => sessionsData?.getSidekickChatSessions ?? [],
    [sessionsData?.getSidekickChatSessions],
  );
  const [sessionsList, setSessionsList] = useState<SidekickChatSession[]>(baseSessions);
  const sessionScopeKey = useMemo(
    () => `${conversationId ?? ''}::${partnerId ?? ''}`,
    [conversationId, partnerId],
  );

  useEffect(() => {
    setSessionsList((prev) => {
      const isNewScope = sessionScopeRef.current !== sessionScopeKey;
      const previousSessions = isNewScope ? [] : prev;
      const sorted = mergeSessions(baseSessions, previousSessions);
      // If we've just refetched and we were in a "fresh chat" state, keep selection null
      // to force explicit choice unless there is only one session.
      if (!selectedSessionId && sorted.length === 1) {
        setSelectedSessionId(sorted[0].sessionRootRunId);
      }
      return sorted;
    });
    sessionScopeRef.current = sessionScopeKey;
  }, [baseSessions, selectedSessionId, sessionScopeKey]);

  const resetSessionState = useCallback(() => {
    setAgentInput('');
    setRunMap({});
    setAssistantByRun({});
    setAgentMessages({});
    setFetchedResults(new Set());
    sessionRunIdsRef.current = new Set();
  }, []);

  useEffect(() => {
    resetSessionState();
    setSelectedSessionId(null);
    sessionRunIdsRef.current = new Set();
    setHistoryOpen(false);
    isStartingNewChatRef.current = true;
    if (conversationId && partnerSelected) {
      void refetchSessions();
    }
  }, [conversationId, partnerSelected, refetchSessions, resetSessionState, setHistoryOpen]);

  const hydrateSessionRuns = useCallback(
    (runs: SidekickSessionRun[]) => {
      resetSessionState();
      if (!hasConversationContext) return;

      const newRunMap: Record<string, SidekickRun> = {};
      const newAgentMessages: Record<string, string> = {};
      const newAssistantByRun: Record<string, AssistantBubbleState> = {};

      runs.forEach((run) => {
        newRunMap[run.runId] = {
          id: run.runId,
          partnerId: partnerId!,
          conversationId: conversationId!,
          userId: '',
          status: run.status,
          aiProviderResponseId: run.aiProviderResponseId ?? null,
          previousResponseId: run.previousResponseId ?? null,
          errorMessage: run.errorMessage ?? null,
          chunkSequence: 0,
          createdAt: run.createdAt,
          updatedAt: run.updatedAt,
        };
        if (run.userMessage?.text || run.userMessage?.html) {
          newAgentMessages[run.runId] = run.userMessage.text || run.userMessage.html || '';
        }
        if (run.assistantMessage?.text || run.assistantMessage?.html) {
          newAssistantByRun[run.runId] = {
            text: run.assistantMessage.text ?? undefined,
            html: run.assistantMessage.html ?? undefined,
            status: run.status,
            errorMessage: run.errorMessage ?? null,
          };
        }
      });

      setRunMap(newRunMap);
      setAgentMessages(newAgentMessages);
      setAssistantByRun(newAssistantByRun);
      sessionRunIdsRef.current = new Set(Object.keys(newRunMap));
    },
    [conversationId, hasConversationContext, partnerId, resetSessionState],
  );

  const handleNewChat = useCallback(() => {
    isStartingNewChatRef.current = true;
    resetSessionState();
    setSelectedSessionId(null);
    setHistoryOpen(false);
    messageInputRef.current?.focus();
  }, [resetSessionState, setHistoryOpen]);

  const loadSession = useCallback(
    (sessionId: string) => {
      if (!hasConversationContext) return;
      resetSessionState();
      setSelectedSessionId(sessionId);
      void loadSessionRuns({
        variables: {
          params: {
            sessionRootRunId: sessionId,
            conversationId: conversationId!,
            partnerId: partnerId!,
            limit: 20,
          },
        },
      });
      setHistoryOpen(false);
    },
    [
      conversationId,
      hasConversationContext,
      loadSessionRuns,
      partnerId,
      resetSessionState,
      setHistoryOpen,
    ],
  );

  const handleLoadSession = useCallback(
    (sessionId: string) => {
      isStartingNewChatRef.current = false;
      loadSession(sessionId);
    },
    [loadSession],
  );

  const historyItems = useMemo<ModalListItem[]>(
    () =>
      sessionsList.map((session) => ({
        id: session.sessionRootRunId,
        title: session.title || session.previewText || 'Chat',
        description: dayjs(session.updatedAt).format('MMM D, h:mm A'),
      })),
    [sessionsList],
  );

  // Matches the chat's title and first message only; the date in the row's description would
  // match "a" or "m" in every chat through AM / PM.
  const matchSession = useCallback<ModalListFilter>(
    (sessionId, search) => {
      const session = sessionsList.find(
        ({ sessionRootRunId }) => sessionRootRunId === sessionId,
      );
      const haystack = `${session?.title ?? ''} ${session?.previewText ?? ''}`.toLowerCase();
      return haystack.includes(search.toLowerCase()) ? 1 : 0;
    },
    [sessionsList],
  );

  const newChatAction = useMemo<ModalListCreateAction>(
    () => ({ label: 'New chat', onSelect: handleNewChat }),
    [handleNewChat],
  );

  useEffect(() => {
    if (
      isStartingNewChatRef.current ||
      selectedSessionId ||
      sessionsList.length === 0 ||
      !hasConversationContext
    ) {
      return;
    }

    const firstSessionId = sessionsList[0]?.sessionRootRunId;
    if (firstSessionId) {
      loadSession(firstSessionId);
    }
  }, [hasConversationContext, loadSession, selectedSessionId, sessionsList]);

  useEffect(() => {
    if (sessionRunsData?.getSidekickSessionRuns) {
      hydrateSessionRuns(sessionRunsData.getSidekickSessionRuns);
    }
  }, [hydrateSessionRuns, sessionRunsData]);

  useEffect(() => {
    if (!hasConversationContext || !conversationId || !selectedSessionId) {
      return;
    }

    const subscription: Subscription = wsEventBus.asObservable().subscribe((event) => {
      if (
        !('data' in event) ||
        !('entityName' in event.data) ||
        event.data.entityName !== 'SidekickRun'
      ) {
        return;
      }
      const attributes = event.data.attributes as unknown as SidekickRun;
      const runId = attributes.id ?? (attributes as { sidekickRunId?: string }).sidekickRunId;

      if (!runId || !sessionRunIdsRef.current.has(runId)) return;
      if (attributes.conversationId && attributes.conversationId !== conversationId) return;

      // @ts-expect-error temp fix for missing fields
      const extra = extractSidekickExtra(event.extraData);
      setRunMap((prev) => {
        const previous = prev[runId];
        const status = extra?.status ?? attributes.status ?? previous?.status;
        const errorMessage =
          extra?.errorMessage ?? attributes.errorMessage ?? previous?.errorMessage;

        return {
          ...prev,
          [runId]: {
            ...(previous || {}),
            ...attributes,
            ...(status ? { status } : {}),
            ...(errorMessage !== undefined ? { errorMessage } : {}),
          },
        };
      });

      if (extra?.chunk) {
        setAssistantByRun((prev) => {
          const nextText = `${prev[runId]?.text ?? ''}${extra.chunk}`;
          return {
            ...prev,
            [runId]: {
              ...(prev[runId] || { status: attributes.status }),
              text: nextText,
            },
          };
        });
      }
      if (extra?.status || extra?.errorMessage) {
        setAssistantByRun((prev) => {
          const status = extra?.status ?? attributes.status ?? prev[runId]?.status;
          const errorMessage =
            extra?.errorMessage ?? attributes.errorMessage ?? prev[runId]?.errorMessage;

          return {
            ...prev,
            [runId]: {
              ...(prev[runId] || { status: attributes.status }),
              ...(status ? { status } : {}),
              ...(errorMessage !== undefined ? { errorMessage } : {}),
            },
          };
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [conversationId, hasConversationContext, selectedSessionId]);

  useEffect(() => {
    const nextFetched = new Set(fetchedResults);
    let changed = false;

    Object.values(runMap)
      .filter((run) => run.status === 'succeeded' && !nextFetched.has(run.id))
      .forEach((run) => {
        changed = true;
        nextFetched.add(run.id);

        void fetchRunResult({
          variables: { params: { runId: run.id, partnerId: run.partnerId } },
        }).then((result) => {
          const payload = result.data?.getSidekickRunResult?.assistantMessage;
          if (!payload) return;
          setAssistantByRun((prev) => ({
            ...prev,
            [run.id]: {
              text: payload.text ?? prev[run.id]?.text,
              html: payload.html ?? prev[run.id]?.html,
              status: run.status,
              errorMessage: run.errorMessage,
            },
          }));
        });
      });

    if (changed) {
      setFetchedResults(nextFetched);
    }
  }, [fetchRunResult, fetchedResults, runMap]);

  const sortedRuns = useMemo(
    () => Object.values(runMap).sort((a, b) => dayjs(a.createdAt).diff(dayjs(b.createdAt))),
    [runMap],
  );

  useEffect(() => {
    scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [assistantByRun, sortedRuns.length]);

  const latestResponseId = useMemo(() => {
    const successfulRuns = sortedRuns.filter((run) => run.status === 'succeeded');
    return successfulRuns[successfulRuns.length - 1]?.aiProviderResponseId;
  }, [sortedRuns]);

  const latestRun = useMemo(() => sortedRuns[sortedRuns.length - 1], [sortedRuns]);

  const isSidekickThinking = useMemo(
    () => latestRun?.status === 'pending' || latestRun?.status === 'streaming',
    [latestRun],
  );

  const isSidekickBusy = isSubmitting || isSending || isSidekickThinking;

  const manualCanSend = Boolean(
    hasConversationContext && agentInput.trim() && !isSidekickBusy,
  );

  const canSendMessage = (message: string) =>
    Boolean(hasConversationContext && message.trim() && !isSidekickBusy);

  const handleSend = async (overrideMessage?: string) => {
    const messageToSend = (overrideMessage ?? agentInput).trim();
    if (isSending || !conversationId || !partnerSelected || !canSendMessage(messageToSend)) {
      return;
    }
    setIsSending(true);
    const previousResponseId = selectedSessionId ? latestResponseId : undefined;

    try {
      const { data } = await startRunMutation({
        variables: {
          input: {
            conversationId,
            partnerId,
            message: messageToSend,
            previousResponseId,
          },
        },
      });
      const run = data?.startSidekickRun;
      if (!run) return;
      setAgentMessages((prev) => ({ ...prev, [run.id]: messageToSend }));
      setRunMap((prev) => ({ ...prev, [run.id]: run }));
      setAssistantByRun((prev) => ({
        ...prev,
        [run.id]: {
          status: run.status,
          text: '',
        },
      }));
      sessionRunIdsRef.current = new Set([...sessionRunIdsRef.current, run.id]);
      if (!selectedSessionId) {
        setSelectedSessionId(run.id);
      }
      setSessionsList((prev) => {
        const sessionId = selectedSessionId ?? run.id;
        const existing = prev.find((session) => session.sessionRootRunId === sessionId);
        const timestamp = run.updatedAt || run.createdAt || dayjs().toISOString();
        const nextSession: SidekickChatSession = {
          sessionRootRunId: sessionId,
          title: existing?.title || existing?.previewText || messageToSend,
          previewText: messageToSend,
          createdAt: existing?.createdAt || timestamp,
          updatedAt: timestamp,
        };
        const filtered = prev.filter((session) => session.sessionRootRunId !== sessionId);
        return [nextSession, ...filtered];
      });
      isStartingNewChatRef.current = false;
      void refetchSessions();
      setAgentInput('');
      messageInputRef.current?.focus();
    } catch (error) {
      console.error('Failed to start Sidekick run', error);
      showErrorAlert('Unable to reach Sidekick. Try again.');
    } finally {
      setIsSending(false);
    }
  };

  if (!hasConversationContext) {
    return (
      <Box
        data-testid="sidekick-ai-chat-tab"
        flex={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography
          variant="body2"
          sx={(theme) => ({ color: theme.palette.custom.OV.sys.onSurfaceVariant })}
        >
          Open a conversation to chat with Sidekick.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Stack
        data-testid="sidekick-ai-chat-tab"
        spacing={2}
        flex={1}
        minHeight={0}
        sx={{ pb: { xs: 'calc(env(safe-area-inset-bottom, 16px) + 12px)', sm: 0 } }}
      >
        <Box
          flex={1}
          overflow="auto"
          sx={{ pb: { xs: 6, sm: 2 }, pr: { xs: 0.5, sm: 0 }, pl: { xs: 0.5, sm: 0 } }}
        >
          <Box
            className="sidekick-thread"
            sx={{
              padding: '0.25rem 0.5rem 0.5rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: -0.5 }}>
              <Tooltip title="Chat history">
                <IconButton
                  aria-label="AI Chat history"
                  data-testid="sidekick-chats-open"
                  size="small"
                  onClick={() => setHistoryOpen(true)}
                  sx={historyIconButtonSx}
                >
                  <DynamicIcon name="history" />
                </IconButton>
              </Tooltip>
            </Stack>
            {isSuggestionsLoading || suggestedQuestions.length > 0 ? (
              <SuggestedQuestions
                questions={suggestedQuestions}
                loading={isSuggestionsLoading}
                onSelect={(question) => void handleSend(question)}
                disabled={!hasConversationContext || isSidekickBusy}
              />
            ) : null}
            {sortedRuns.length === 0 ? (
              <Typography
                variant="body2"
                textAlign="center"
                sx={(theme) => ({ color: theme.palette.custom.OV.sys.onSurfaceVariant })}
              >
                Ask Sidekick for help composing your next reply.
              </Typography>
            ) : (
              sortedRuns.map((run) => (
                <Fragment key={run.id}>
                  {agentMessages[run.id] ? (
                    <MessageWithMeta
                      align="right"
                      timestamp={run.createdAt}
                      partnerId={partnerId}
                      label="You"
                    >
                      <Bubble
                        align="right"
                        onCopy={() => copyToClipboard(agentMessages[run.id])}
                      >
                        {agentMessages[run.id]}
                      </Bubble>
                    </MessageWithMeta>
                  ) : null}
                  <MessageWithMeta
                    align="left"
                    timestamp={run.updatedAt || run.createdAt}
                    partnerId={partnerId}
                    label="Sidekick"
                  >
                    <Bubble
                      align="left"
                      onCopy={() => {
                        const assistantBubble = assistantByRun[run.id];
                        const contentToCopy = getAssistantCopyContent(assistantBubble);
                        if (contentToCopy) void copyToClipboard(contentToCopy);
                      }}
                    >
                      <AssistantContent run={run} bubble={assistantByRun[run.id]} />
                    </Bubble>
                  </MessageWithMeta>
                </Fragment>
              ))
            )}
            <Box ref={scrollAnchorRef} />
          </Box>
        </Box>
        <Stack sx={{ pb: { xs: 'env(safe-area-inset-bottom, 12px)', sm: 0 } }}>
          <Box sx={getChatInputContainerSx(isInputFocused)}>
            <InputBase
              inputRef={messageInputRef}
              multiline
              minRows={1}
              maxRows={6}
              value={agentInput}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              onChange={(event) => setAgentInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  void handleSend();
                }
              }}
              placeholder="Ask Sidekick anything…"
              sx={chatInputSx}
            />
            <Stack direction="row" alignItems="center" spacing={0.25}>
              {conversationId ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={conversationContextSx}
                >
                  <DynamicIcon name="forum" sx={{ fontSize: '0.9375rem', flexShrink: 0 }} />
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ minWidth: 0, fontSize: '0.71875rem', fontWeight: 600 }}
                  >
                    {friendlyConversationId || conversationId}
                    {conversationSubject ? `: ${conversationSubject}` : ''}
                  </Typography>
                </Stack>
              ) : null}
              <Box sx={{ flex: 1 }} />
              <IconButton
                aria-label="Send message"
                onClick={() => void handleSend()}
                disabled={!manualCanSend}
                sx={getSendButtonSx(manualCanSend)}
              >
                {isSidekickBusy ? (
                  <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} />
                ) : (
                  <DynamicIcon name="arrow_upward" />
                )}
              </IconButton>
            </Stack>
          </Box>
        </Stack>
      </Stack>
      <ModalContainer
        open={historyOpen}
        size="medium"
        title="Sidekick chats"
        onClose={() => setHistoryOpen(false)}
        data-testid="sidekick-chats-modal"
      >
        <ModalList
          items={historyItems}
          filter={matchSession}
          onSelect={(item) => handleLoadSession(item.id)}
          loading={sessionsLoading}
          placeholder="Search chats"
          emptyText="No chats found"
          createAction={newChatAction}
        />
      </ModalContainer>
    </>
  );
};
